/** @format */

import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom"; // Added useLocation
import { Helmet } from "react-helmet";
import { motion, AnimatePresence } from "framer-motion";
import {
	CheckCircle,
	ShoppingBag,
	Mail,
	Clock,
	HelpCircle,
	XCircle,
} from "lucide-react";

// Actions & Components (Ensure paths are correct)
import { resetCart } from "../actions/cartActions";
import { LoadingSpinner } from "../components/LoadingSpinner"; // Assuming path
import { OrderDetails } from "../components/OrderDetails"; // Assuming path
import { Toast } from "./customize/Toast";
import {
	addEmailToCartMailChimp,
	apiMail,
	removeEmailFromCartMailChimp,
} from "../actions/userActions";
import { OrderConfirmation } from "../emails/new";

const ConfirmationScreen = () => {
	const dispatch = useDispatch();
	const location = useLocation();

	// Get orderId passed from previous screen
	const orderId = location.state?.orderId;

	// State for loading and order data specific to this screen
	const [isLoading, setIsLoading] = useState(true);
	const [toast, setToast] = useState(null); // Toast notification state
	const { userInfo } = useSelector((state) => state.userLogin); // Maybe needed for "My Orders" link visibility
	const [fetchError, setFetchError] = useState(null);
	const [confirmedOrder, setConfirmedOrder] = useState(null); // State to hold fetched order details
	const cartItems = useSelector((state) => state.cart.cartItems);
	const shippingAddress = useSelector((state) => state.cart.shippingAddress);
	const cartSummary = useSelector((state) => state.cart.cartSummary);
	const paymentResult = useSelector((state) => state.cart.paymentResult);

	// --- Selectors (Only needed if displaying something not in fetched order, e.g., country for OrderDetails?) ---
	// const country = useSelector((state) => state.country.country);
	// const { userInfo } = useSelector((state) => state.userLogin); // Maybe needed for "My Orders" link visibility

	// --- Effect to Fetch Order Details ---
	useEffect(() => {
		if (!orderId) {
			console.error("ConfirmationScreen: No orderId found in location state.");
			setFetchError(
				"Order details could not be loaded. Please check your email or contact support."
			);
			setIsLoading(false);
			// Optionally redirect back to cart or home after a delay
			// setTimeout(() => navigate('/'), 3000);
			return;
		}

		const fetchDetails = async () => {
			setIsLoading(true);
			setFetchError(null);
			try {
				// Dispatch action to fetch order by ID - assumes it returns the order data
				// or updates a specific slice of Redux state (e.g., state.orderDetails)
				// *** Store fetched data in component state ***
				setConfirmedOrder({
					insertId: orderId, // Assuming orderId is the order ID
					orderDate: new Date().toISOString(), // Assuming no order date in response
					paymentResult,
					cartItems,
					shippingAddress,
					cartSummary,
				}); // Assuming action returns the order object directly

				// --- Post-Success Actions (Run only once after successful fetch) ---
				// 1. Reset the main shopping cart
				dispatch(resetCart());

				// 2. Mailchimp/Email (Ideally moved to backend)
				// Trigger server-side email/mailchimp based on order success if possible.
				// If MUST be frontend:
				const emailToSend =
					confirmedOrder?.shippingAddress?.email || userInfo?.email;
				if (emailToSend) {
					removeEmailFromCartMailChimp(emailToSend); // Remove from abandoned cart list
					addEmailToCartMailChimp(emailToSend, "Purchase"); // Add to purchased list
					const productList = cartItems.map((item) => {
						const newColour = item.productInfo?.New_Colour;
						return {
							img: item.productInfo?.Program.includes("Custom")
								? `https://offcutshades.com/images/customized.png`
								: `https://offcutshades.com/images/programs/${item.productInfo?.Program}/window/${item.optionInfo?.selectedColor}.jpg`
										.replaceAll("%", "")
										.replaceAll(" ", "_")
										.toLowerCase(),
							name: `${
								item.productInfo?.New_Program ?? item.productInfo?.Program
							} ${newColour ? `(${newColour})` : ""} ${parseFloat(item.optionInfo?.widthWhole).toFixed(2)}" W x ${parseFloat(item.optionInfo?.lengthWhole).toFixed(2)}" H`,
							price: item.optionInfo.RETAIL_PRICE,
							quantity: item.optionInfo.quantity,
						};
					});
					apiMail(
						emailToSend,
						"Thank you for shopping with us! Enjoy your shades!",
						<OrderConfirmation
							orderDate={new Date().toISOString()}
							orderNumber={orderId}
							productList={productList}
							prices={{
								totalMsrp: parseFloat(cartSummary.FINAL_MSRP),
								totalDiscount: parseFloat(cartSummary.SAVINGS) || 0,
								promoCode: cartSummary.promoCode,
								subTotal: parseFloat(cartSummary.SUBTOTAL),
								total: parseFloat(cartSummary.TOTAL),
								shipping: parseFloat(cartSummary.shipping),
								withPromoPrice: parseFloat(cartSummary.withPromoPrice),
								tax: parseFloat(cartSummary.FINAL_TAX),
							}}
							address={`${shippingAddress.addressLine1}${
								shippingAddress.addressLine2
									? ` - ${shippingAddress.addressLine2}`
									: ""
							}, ${shippingAddress.city}, ${shippingAddress.province}, ${
								shippingAddress.postalCode
							}, ${shippingAddress.country}`}
							name={`${shippingAddress.firstName} ${shippingAddress.lastName}`}
							phoneNumber={shippingAddress.phoneNumber}
						/>
					);
				}
			} catch (err) {
				console.error("Error fetching order details:", err);
				setFetchError(
					`Failed to load order confirmation: ${
						err.message || "Please contact support."
					}`
				);
				setConfirmedOrder(null);
			} finally {
				setIsLoading(false);
			}
		};

		fetchDetails();

		// Cleanup function? Usually not needed unless subscribing to something.
		// return () => { /* Cleanup logic */ };
	}, [orderId, dispatch]); // Depend only on orderId and dispatch

	// --- Render Logic ---

	if (isLoading) {
		return (
			<div className="min-h-screen flex items-center justify-center bg-gray-50">
				<LoadingSpinner text="Loading your order confirmation..." />
			</div>
		);
	}
	return (
		<>
			<Helmet>
				<title>Order Confirmed! | Off Cut Shades</title>
				<meta name="robots" content="noindex,nofollow" />{" "}
				{/* Keep search engines away */}
			</Helmet>

			<div className="min-h-screen bg-gradient-to-b from-green-50 via-gray-50 to-gray-100 py-10 md:py-16">
				{" "}
				{/* Added subtle gradient */}
				<div className="container mx-auto px-4 max-w-3xl">
					{" "}
					{/* Centered and constrained width */}
					<AnimatePresence>
						{
							fetchError ? (
								// --- Error State ---
								<motion.div
									key="error"
									initial={{ opacity: 0, y: 10 }}
									animate={{ opacity: 1, y: 0 }}
									exit={{ opacity: 0 }}
									className="bg-white rounded-xl shadow-lg border border-red-200 p-8 text-center flex flex-col items-center"
								>
									<XCircle
										className="w-16 h-16 text-red-400 mb-5"
										strokeWidth={1.5}
									/>
									<h1 className="text-2xl font-semibold text-red-700 mb-3">
										Order Confirmation Error
									</h1>
									<p className="text-gray-600 mb-6">{fetchError}</p>
									<div className="flex gap-4">
										<Link to="/shop" className="btn btn-secondary">
											Continue Shopping
										</Link>
										<Link to="/contact" className="btn btn-primary-outline">
											Contact Support
										</Link>
									</div>
								</motion.div>
							) : confirmedOrder ? (
								// --- Success State ---
								<motion.div
									key="success"
									initial={{ opacity: 0, y: 10 }}
									animate={{ opacity: 1, y: 0 }}
								>
									<div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6 md:p-8 mb-8">
										{/* Success Header */}
										<div className="text-center mb-6 pb-6 border-b border-gray-100">
											<motion.div
												initial={{ scale: 0.5, opacity: 0 }}
												animate={{ scale: 1, opacity: 1 }}
												transition={{
													type: "spring",
													stiffness: 200,
													damping: 15,
													delay: 0.1,
												}}
												className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4"
											>
												<CheckCircle className="w-9 h-9 text-green-500" />
											</motion.div>
											<h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
												Order Confirmed!
											</h1>
											{/* Display Order ID Clearly */}
											<p className="text-sm text-gray-500">
												Order ID:{" "}
												<span className="font-medium text-gray-700">
													{confirmedOrder.insertId || orderId || "N/A"}
												</span>
											</p>
										</div>

										{/* What Happens Next */}
										<div className="mb-6 text-sm text-gray-700 bg-blue-50 p-4 rounded-lg border border-blue-200 space-y-2">
											<h2 className="font-semibold text-base text-gray-800 flex items-center gap-2">
												<Mail size={16} className="text-blue-600" />{" "}
												Confirmation Email Sent
											</h2>
											<p>
												We've sent a confirmation email to{" "}
												<strong className="break-all">
													{confirmedOrder?.shippingAddress?.email ||
														"your email address"}
												</strong>{" "}
												with your order details. Please check your inbox (and
												spam folder!).
											</p>
											<h2 className="font-semibold text-base text-gray-800 pt-2 flex items-center gap-2">
												<Clock size={16} className="text-blue-600" /> Production
												& Shipping
											</h2>
											<p>
												Your custom shades will now enter production. Estimated
												production time is typically 2-3 business days. You'll
												receive another email once your order ships.
											</p>
										</div>

										{/* Order Details Component */}
										{/* Pass the fetched, confirmed order data */}
										<OrderDetails
											order={confirmedOrder} // Pass the whole fetched order object
											// paymentResult={confirmedOrder.paymentResult} // If paymentResult is part of fetched order
											// country={country} // Pass country if needed by OrderDetails
										/>
									</div>

									{/* Action Buttons */}
									<div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-8">
										<Link
											to="/shop" // Link back to main shop
											className="btn btn-secondary w-full sm:w-auto" // Consistent button style
										>
											Continue Shopping
										</Link>
										{/* Link to My Orders only if user might be logged in */}
										{userInfo && ( // Or check if confirmedOrder has user ID associated
											<Link
												to="/myorders" // Assuming this is the route
												className="btn btn-primary w-full sm:w-auto" // Outline style
											>
												<ShoppingBag size={18} /> View My Orders
											</Link>
										)}
										<Link
											to="/contact"
											className="text-xs text-gray-600 hover:text-primary inline-flex items-center gap-1 sm:ml-4"
										>
											<HelpCircle size={14} /> Need Help?
										</Link>
									</div>
								</motion.div>
							) : null /* Should not happen if loading/error handle correctly */
						}
					</AnimatePresence>
				</div>
			</div>

			{/* --- Toast Notifications --- */}
			<AnimatePresence>
				{" "}
				{toast && (
					<Toast
						show={!!toast}
						type={toast.type}
						message={toast.message}
						onClose={() => setToast(null)}
						duration={4000}
					/>
				)}{" "}
			</AnimatePresence>
		</>
	);
};

export default ConfirmationScreen;
